import { PromotionPayload } from '../../../api/generated';

/**
 * Checks if two date ranges overlap.
 * Returns true if ranges overlap or if either range is unbounded (null).
 */
const dateRangesOverlap = (
  start1: string | null | undefined,
  end1: string | null | undefined,
  start2: string | null | undefined,
  end2: string | null | undefined
): boolean => {
  // If either promotion has no date constraints, they could overlap
  if ((!start1 && !end1) || (!start2 && !end2)) {
    return true;
  }

  const s1 = start1 ? new Date(start1).getTime() : -Infinity;
  const e1 = end1 ? new Date(end1).getTime() : Infinity;
  const s2 = start2 ? new Date(start2).getTime() : -Infinity;
  const e2 = end2 ? new Date(end2).getTime() : Infinity;

  // Ranges overlap if one starts before the other ends
  return s1 <= e2 && s2 <= e1;
};

/**
 * Parses time of day string (HH:mm:ss or HH:mm) to minutes since midnight.
 */
const parseTimeToMinutes = (time: string | null | undefined): number | null => {
  if (!time) return null;
  const parts = time.split(':');
  if (parts.length < 2) return null;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (isNaN(hours) || isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

/**
 * Checks if two time-of-day ranges overlap.
 * Returns true if ranges overlap or if either range is unbounded (null).
 */
const timeRangesOverlap = (
  min1: string | null | undefined,
  max1: string | null | undefined,
  min2: string | null | undefined,
  max2: string | null | undefined
): boolean => {
  // If either promotion has no time constraints, they could overlap
  if ((!min1 && !max1) || (!min2 && !max2)) {
    return true;
  }

  const m1 = parseTimeToMinutes(min1);
  const x1 = parseTimeToMinutes(max1);
  const m2 = parseTimeToMinutes(min2);
  const x2 = parseTimeToMinutes(max2);

  // Use full day range if null
  const start1 = m1 !== null ? m1 : 0;
  const end1 = x1 !== null ? x1 : 24 * 60;
  const start2 = m2 !== null ? m2 : 0;
  const end2 = x2 !== null ? x2 : 24 * 60;

  // Ranges overlap if one starts before the other ends
  return start1 < end2 && start2 < end1;
};

/**
 * Checks if two transaction amount ranges overlap.
 * Returns true if ranges overlap or if either range is unbounded (null).
 */
const amountRangesOverlap = (
  min1: number | null | undefined,
  max1: number | null | undefined,
  min2: number | null | undefined,
  max2: number | null | undefined
): boolean => {
  // If either promotion has no amount constraints, they could overlap
  const hasRange1 = min1 !== null && min1 !== undefined || max1 !== null && max1 !== undefined;
  const hasRange2 = min2 !== null && min2 !== undefined || max2 !== null && max2 !== undefined;

  if (!hasRange1 || !hasRange2) {
    return true;
  }

  const start1 = min1 !== null && min1 !== undefined ? min1 : 0;
  const end1 = max1 !== null && max1 !== undefined ? max1 : Infinity;
  const start2 = min2 !== null && min2 !== undefined ? min2 : 0;
  const end2 = max2 !== null && max2 !== undefined ? max2 : Infinity;

  // Ranges overlap if one starts before or at the point the other ends
  return start1 <= end2 && start2 <= end1;
};

/**
 * Checks if two promotions overlap based on all their constraints.
 * Two promotions overlap if all of their constraint ranges overlap:
 * - Date/time ranges overlap
 * - Time of day ranges overlap
 * - Transaction amount ranges overlap
 */
export const promotionsOverlap = (
  promo1: PromotionPayload,
  promo2: PromotionPayload
): boolean => {
  const datesOverlap = dateRangesOverlap(
    promo1.startDateTime,
    promo1.endDateTime,
    promo2.startDateTime,
    promo2.endDateTime
  );

  const timesOverlap = timeRangesOverlap(
    promo1.minTimeOfDay,
    promo1.maxTimeOfDay,
    promo2.minTimeOfDay,
    promo2.maxTimeOfDay
  );

  const amountsOverlap = amountRangesOverlap(
    promo1.minTransactionAmount,
    promo1.maxTransactionAmount,
    promo2.minTransactionAmount,
    promo2.maxTransactionAmount
  );

  // All constraints must overlap for promotions to overlap
  return datesOverlap && timesOverlap && amountsOverlap;
};

/**
 * Checks if any promotions in the list overlap with each other.
 * Returns true if at least one pair of promotions overlaps.
 */
export const hasOverlappingPromotions = (promotions: PromotionPayload[]): boolean => {
  if (promotions.length < 2) {
    return false;
  }

  for (let i = 0; i < promotions.length; i++) {
    for (let j = i + 1; j < promotions.length; j++) {
      if (promotionsOverlap(promotions[i], promotions[j])) {
        return true;
      }
    }
  }

  return false;
};
